import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'

import { ApiError } from '@/api/client'
import { authApi, partnerApi } from '@/api/endpoints'
import { keys } from '@/api/queries'
import { Avatar } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import { TextArea, TextField } from '@/components/ui/Field'
import { PageHeader, Section } from '@/components/ui/Page'
import { StatusPill } from '@/components/ui/StatusPill'
import { describeError } from '@/lib/errors'
import { date, pct } from '@/lib/format'
import { useAuthStore } from '@/store/auth'
import { toast } from '@/store/toast'

/** Mirrors PASSWORD_MIN_LENGTH on the server. */
const PASSWORD_MIN = 12

export function AccountPage() {
  const cached = useAuthStore((state) => state.partner)
  const setPartner = useAuthStore((state) => state.setPartner)
  const me = useQuery({ queryKey: keys.me, queryFn: partnerApi.me })
  const partner = me.data ?? cached

  useEffect(() => {
    if (me.data) setPartner(me.data)
  }, [me.data, setPartner])

  return (
    <>
      <PageHeader title="Account" description="Your partner profile, how to reach you and where to send your payouts." />
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
        <Section className="self-start" title="Profile" description="Set by JobSlide. Ask your contact to change any of it." bodyClassName="p-0">
          <div className="flex items-center gap-3 border-b border-line px-5 py-4">
            <Avatar name={partner?.name} size="lg" />
            <div className="min-w-0">
              <p className="truncate text-[16px] font-semibold text-ink">{partner?.name}</p>
              <p className="truncate text-[13px] text-ink-muted">{partner?.company || 'No company on file'}</p>
            </div>
            {partner && (
              <StatusPill tone={partner.status === 'active' ? 'success' : 'danger'} className="ml-auto">
                {partner.status === 'active' ? 'Active' : 'Suspended'}
              </StatusPill>
            )}
          </div>
          <dl className="divide-y divide-line">
            <Row label="Email" value={partner?.email} />
            <Row label="Partner tag" value={partner?.tag} mono />
            <Row label="Maximum discount" value={partner ? pct(partner.maxDiscountPct) : undefined} mono />
            <Row label="Partner since" value={partner ? date(partner.createdAt) : undefined} />
          </dl>
        </Section>

        <div className="grid content-start gap-4">
          <ContactForm />
          <PasswordForm />
        </div>
      </div>
    </>
  )
}

function ContactForm() {
  const partner = useAuthStore((state) => state.partner)
  const setPartner = useAuthStore((state) => state.setPartner)
  const [phone, setPhone] = useState(partner?.phone ?? '')
  const [payout, setPayout] = useState(partner?.payoutDetails ?? '')
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    setPhone(partner?.phone ?? '')
    setPayout(partner?.payoutDetails ?? '')
  }, [partner?.phone, partner?.payoutDetails])

  const save = useMutation({
    mutationFn: () => partnerApi.updateMe({ phone: phone.trim(), payoutDetails: payout.trim() }),
    onSuccess: (updated) => {
      setPartner(updated)
      setErrors({})
      toast.success('Details saved')
    },
    onError: (error) => {
      if (error instanceof ApiError) setErrors(error.fieldErrors)
      const { title, description } = describeError(error, 'Could not save your details')
      toast.error(title, description)
    },
  })

  const dirty = phone.trim() !== (partner?.phone ?? '') || payout.trim() !== (partner?.payoutDetails ?? '')

  return (
    <Section title="Contact and payout" description="JobSlide uses these to reach you and to pay your settlements.">
      <form
        className="grid gap-4"
        onSubmit={(event) => {
          event.preventDefault()
          save.mutate()
        }}
      >
        <TextField appearance="box" label="Phone" type="tel" autoComplete="tel" maxLength={40} placeholder="+91 98765 43210" value={phone} onChange={(event) => setPhone(event.target.value)} error={errors.phone || null} />
        <TextArea
          appearance="box"
          label="Payout details"
          rows={4}
          placeholder={'e.g. UPI ravi@okaxis\nor bank: account name, number, IFSC / SWIFT'}
          value={payout}
          onChange={(event) => setPayout(event.target.value)}
          error={errors.payoutDetails || null}
          hint="Only you and JobSlide admins can see this."
        />
        <div className="flex justify-end">
          <Button type="submit" size="sm" loading={save.isPending} disabled={!dirty}>
            Save details
          </Button>
        </div>
      </form>
    </Section>
  )
}

function PasswordForm() {
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [errors, setErrors] = useState<Record<string, string>>({})

  const change = useMutation({
    mutationFn: () => authApi.changePassword({ currentPassword: current, newPassword: next }),
    onSuccess: () => {
      setCurrent('')
      setNext('')
      setConfirm('')
      setErrors({})
      toast.success('Password changed', 'Use the new one next time you sign in.')
    },
    onError: (error) => {
      if (error instanceof ApiError && (error.code === 'invalid_credentials' || error.code === 'invalid_password' || error.status === 400)) {
        setErrors({ current: 'That is not your current password.' })
        return
      }
      if (error instanceof ApiError && Object.keys(error.fieldErrors).length) {
        setErrors({ current: error.fieldErrors.currentPassword ?? '', next: error.fieldErrors.newPassword ?? '' })
      }
      const { title, description } = describeError(error, 'Could not change your password')
      toast.error(title, description)
    },
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    const found: Record<string, string> = {}
    if (!current) found.current = 'Enter your current password.'
    if (next.length < PASSWORD_MIN) found.next = `At least ${PASSWORD_MIN} characters.`
    else if (next === current) found.next = 'Pick a password you have not used here.'
    if (confirm !== next) found.confirm = 'The two passwords do not match.'
    setErrors(found)
    if (Object.keys(found).length === 0) change.mutate()
  }

  return (
    <Section title="Change password">
      <form className="grid gap-4" onSubmit={submit} noValidate>
        <TextField appearance="box" label="Current password" type="password" autoComplete="current-password" value={current} onChange={(event) => setCurrent(event.target.value)} error={errors.current || null} />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField appearance="box" label="New password" type="password" autoComplete="new-password" value={next} onChange={(event) => setNext(event.target.value)} error={errors.next || null} hint={`At least ${PASSWORD_MIN} characters`} />
          <TextField appearance="box" label="Repeat new password" type="password" autoComplete="new-password" value={confirm} onChange={(event) => setConfirm(event.target.value)} error={errors.confirm || null} />
        </div>
        <div className="flex justify-end">
          <Button type="submit" size="sm" variant="secondary" loading={change.isPending} disabled={!current || !next || !confirm}>
            Change password
          </Button>
        </div>
      </form>
    </Section>
  )
}

function Row({ label, value, mono }: { label: string; value?: string; mono?: boolean }) {
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-3">
      <dt className="text-[13.5px] text-ink-muted">{label}</dt>
      <dd className={`min-w-0 truncate text-right text-[14px] text-ink ${mono ? 'num font-medium' : ''}`}>{value ?? '—'}</dd>
    </div>
  )
}
